import {
    ACCOUNT_GET_V2,
    BOARD_ACCESS_GET_V2,
    BOARD_MEMBERS_ACCESS_SET_V2,
    BOARD_MEMBERS_ADD_V2,
    BOARD_MEMBERS_DELETE_V2,
    BOARD_SHARE_GET_V2,
    BOARD_SHARE_LINK_ACCESS_PASSWORD_GET_V2,
    BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2,
    BOARD_SHARE_LINK_ACCESS_SET_V2,
    BOARD_SHARE_TEAM_ACCESS_SET,
    PROJECT_ACCESS_GET_V2,
    PROJECT_MEMBERS_ACCESS_SET_V2,
    PROJECT_MEMBERS_ADD_V2,
    PROJECT_MEMBERS_DELETE_V2,
    PROJECT_NAME_SET_V2,
    PROJECT_REMOVE_V2,
    PROJECT_SHARE_GET_V2,
    TEAM_MEMBERS_FIND_V2,
} from "@/api/apiEndpoints";
import {
    applySearchBubbleTemplate,
    applyUserTemplate,
    getBoardShareAdditionalTemplate,
    getMenuTemplate,
    getPasswordAddTemplate,
    getPasswordChangeTemplate,
    getPasswordEditorTemplate,
    getProjectShareTopTemplate,
    getSearchAdditionalTemplate,
} from "@/appInterface/windows/shareWindow/templateHelpers";
import {
    type AccessByLinkOrTeam,
    type AccountInfoResponse,
    type AdditionalData,
    type BoardAccessResponse,
    type BoardModeData,
    type BoardShareResponse,
    type MapMembers,
    type MemberMap,
    type PasswordGetResponse,
    type ProjectShareResponse,
    ReasonAccess,
    type State,
} from "@/appInterface/windows/shareWindow/types";
import { MakeRequest } from "@/canv/utils";
import {
    getAccessMenuData,
    getAccessReadable,
    getCandidatesList,
    getTargetLocal,
    InitAccessToggles,
    notify,
    validatePassword,
} from "./shareWindowHelpers.js";

export class ShareWindow {
    constructor() {
        this.data = {
            mode: "board",
            token: "",
            accessSelector: (data: any) => {},
            host: "",
            HTTPRequest: MakeRequest,
            stateChange: () => {},
            notificator: (text: string) => {},
            searchInputState: (state: number) => {},
            boardUID: "",
            onlyRead: false,
            projectId: 0,
        };
    }

    async open(data: BoardModeData) {
        /*
            Общий sharing window для board sharing и project sharing (project sharing НЕ ИСПОЛЬЗУЕТСЯ)
            data = {
                Первый блок надо передать полностью [board sharing], [project sharing]
                "mode": "board" | "project"
                "token": "",
                "accessSelector": func(),
                "host": "", [used in board sharing only],
                "HTTPRequest": func(),
                "stateChange": func(), вызывается при изменении названия или удалении/покидании проекта, покидания доски


                Этот блок нужно передавать только при [board sharing]
                "boardUID": "",

                Этот блок нужно передавать только при [project sharing]
                "projectId": int,

                [optional] поля
                "searchInputState": func(),  нужен на доске для переключения inputFocused
                "notificator": func(),
            }

            this.additionalData {
                произвольные поля
            }
        */

        this.state = "start";

        this.data = { ...this.data, ...data };

        const accountInfo: AccountInfoResponse = await MakeRequest(ACCOUNT_GET_V2, {
            token: this.data.token,
            boardUID: this.data.boardUID,
        });

        if (data.mode === "board") {
            const result: BoardShareResponse = await MakeRequest(BOARD_SHARE_GET_V2, {
                token: this.data.token,
                boardUID: this.data.boardUID,
            });

            for (let i = 0; i < result.members.length; i++) {
                const member = result.members[i];
                this.members.set(member.accountId, {
                    memberId: member.accountId,
                    name: member.name,
                    email: member.email,
                    access: member.access,
                });
            }

            this.additionalData = {
                accessByTeam: result.accessByTeam,
                accessByLink: result.accessByLink,
                allowControlByLink: result.allowControlByLink,
                teamName: result.teamName,
                searchAccess: "edit",
                teamId: result.teamId,
            };
        } else {
            const projectShare: ProjectShareResponse = await MakeRequest(PROJECT_SHARE_GET_V2, {
                token: this.data.token,
                projectId: this.data.projectId,
            });

            for (let i = 0; i < projectShare.members.length; i++) {
                const member = projectShare.members[i];
                this.members.set(member.accountId, {
                    memberId: member.accountId,
                    name: member.name,
                    email: member.email,
                    access: member.access,
                });
            }

            this.additionalData = {
                myAccountId: Number(accountInfo.id),
                searchAccess: "edit",
                projectName: projectShare.projectName,
                teamId: projectShare.teamId,
            };
        }

        await this.updateMyAccess();

        const shareWindowWrapper = document.createElement("div");
        shareWindowWrapper.classList.add("shareWindowWrapper");
        if (this.additionalData && "teamName" in this.additionalData) {
            shareWindowWrapper.innerHTML += getMenuTemplate(
                getAccessReadable(this.additionalData.searchAccess),
            );
        }
        document.body.prepend(shareWindowWrapper);

        this.BuildShare();
        this.init();
    }

    init() {
        // wrapper
        const markShareWindowWrapper = document.querySelector(".shareWindowWrapper");
        if (markShareWindowWrapper) {
            markShareWindowWrapper.addEventListener("mousedown", this.wrapperClick, false);
        }
        // search input change
        const markSearch = document.querySelector(".shareWindowSearchFieldInput");
        if (markSearch) {
            markSearch.addEventListener("input", this.searchChange, false);
            markSearch.addEventListener("keypress", this.searchInput, false);

            markSearch.addEventListener("focus", () => this.inputState(1), false);
            markSearch.addEventListener("blur", () => this.inputState(0), false);
        }
        // action/minor
        const markActionButton = document.querySelector(".shareWindowActionsItemAction");
        if (markActionButton) {
            markActionButton.addEventListener("mousedown", this.actionClick, false);
        }
        const markMinorButton = document.querySelector(".shareWindowActionsItemMinor");
        if (markMinorButton) {
            markMinorButton.addEventListener("mousedown", this.minorClick, false);
        }
    }

    inputState = (value: number) => {
        if (this.data.searchInputState) {
            this.data.searchInputState(value);
        }
    };

    close() {
        const markWrapper = document.querySelector(".shareWindowWrapper");
        if (markWrapper) {
            markWrapper.outerHTML = "";
        }
    }

    /* Builds -------------------------- */

    BuildShare() {
        this.state = "start";

        const markPeopleList = document.querySelector(".shareWindowPeopleList");
        if (markPeopleList) {
            markPeopleList.classList.remove("shareWindowPeopleListFixedHeight");
        }
        const shareWindowSearchToggle = document.querySelector(".shareWindowSearchToggle");
        if (shareWindowSearchToggle) {
            shareWindowSearchToggle.classList.remove("shareWindowSearchToggleOn");
        }
        const shareWindowSearch = document.querySelector(".shareWindowSearch");
        if (shareWindowSearch) {
            shareWindowSearch.classList.remove("shareWindowSearchHide");

            if (
                this.data.mode === "project" &&
                this.additionalData &&
                this.additionalData.myAccess === "view"
            ) {
                shareWindowSearch.classList.add("shareWindowSearchHide");
            }
        }
        // change people list padding
        const shareWindowPeople = document.querySelector(".shareWindowPeople");
        if (shareWindowPeople?.classList.contains("shareWindowPeopleSearch")) {
            shareWindowPeople.classList.remove("shareWindowPeopleSearch");
        }
        // search remove toggle
        const shareWindowSearchFieldInput = document.querySelector(".shareWindowSearchFieldInput");
        if (shareWindowSearchFieldInput) {
            if (this.data.mode === "board") {
                shareWindowSearchFieldInput.setAttribute("placeholder", "Пригласить на доску");
            } else {
                shareWindowSearchFieldInput.setAttribute("placeholder", "Пригласить в проект");
            }
        }

        //  Готовим additional
        if (this.data.mode === "board") {
            // setting additional
            const markAdditional = document.querySelector(".shareWindowShareAdditional");
            if (markAdditional && this.additionalData && "teamName" in this.additionalData) {
                markAdditional.innerHTML = getBoardShareAdditionalTemplate(
                    this.additionalData.teamName,
                );
            }
            // refill accessByTeam
            const markAccessByTeam = document.querySelector(
                ".shareWindowShareAdditionalMoreOptionToggleInfoTeam",
            );
            if (markAccessByTeam && this.additionalData && "accessByTeam" in this.additionalData) {
                markAccessByTeam.innerHTML = getAccessReadable(this.additionalData.accessByTeam);
            }
            // refill accessByLink
            const markAccessByLink = document.querySelector(
                ".shareWindowShareAdditionalMoreOptionToggleInfoLink",
            );
            if (markAccessByLink && this.additionalData && "accessByLink" in this.additionalData) {
                markAccessByLink.innerHTML = getAccessReadable(this.additionalData.accessByLink);
            }

            if (
                this.additionalData &&
                "allowControlByLink" in this.additionalData &&
                this.additionalData.allowControlByLink === "deny"
            ) {
                const shareWindowItems = document.querySelector(".shareWindowShareAdditionalMore");
                if (shareWindowItems) {
                    const secondBlockAccessByLink = shareWindowItems.getElementsByClassName(
                        "shareWindowShareAdditionalMoreOption",
                    )[1];
                    secondBlockAccessByLink.classList.add("hideAccessLink");
                }
            }
            const markActionButton = document.querySelector(".shareWindowActionsItemAction");
            if (markActionButton) {
                markActionButton.innerHTML = "Скопировать ссылку на доску";
            }
            const markMinorButton = document.querySelector(".shareWindowActionsItemMinor");
            if (markMinorButton) {
                markMinorButton.innerHTML = "Закрыть";
            }
            this.reloadPasswordSpace();
        } else {
            // setting top
            const markTop = document.querySelector(".shareWindowTop");
            if (markTop && this.additionalData && "projectName" in this.additionalData) {
                markTop.innerHTML = getProjectShareTopTemplate(this.additionalData);
            }
            const markAdditional = document.querySelector(".shareWindowShareAdditional");
            if (markAdditional) {
                markAdditional.innerHTML = "";
            }
            const markActionButton = document.querySelector(".shareWindowActionsItemAction");
            if (markActionButton) {
                markActionButton.innerHTML = "Готово";
            }
            const markMinorButton = document.querySelector(".shareWindowActionsItemMinor");
            if (markMinorButton) {
                if (this.additionalData && this.additionalData.myAccess === "owner") {
                    markMinorButton.innerHTML = "Удалить проект";
                } else {
                    markMinorButton.innerHTML = "Покинуть проект";
                }
            }
        }
        // Меняем title списка
        const shareWindowPeopleTitle = document.querySelector(".shareWindowPeopleTitle");
        if (shareWindowPeopleTitle) {
            shareWindowPeopleTitle.innerHTML = "Настройка доступа";
        }
        this.refillCurrentMembers(this.members);
        /* initing */
        InitAccessToggles(this.accessToggle);
    }

    async BuildSearch() {
        if (this.state === "start") {
            // Переход стейтов
            // Чистим top
            const shareWindowTop = document.querySelector(".shareWindowTop");
            if (shareWindowTop) {
                shareWindowTop.innerHTML = "";
            }
            // Готовим additional блок
            const shareWindowShareAdditional = document.querySelector(
                ".shareWindowShareAdditional",
            );
            if (shareWindowShareAdditional) {
                shareWindowShareAdditional.innerHTML = getSearchAdditionalTemplate();
            }
            // Фиксируем высоту candidates list
            const markPeopleList = document.querySelector(".shareWindowPeopleList");
            if (markPeopleList) {
                markPeopleList.classList.add("shareWindowPeopleListFixedHeight");
            }
            // Включаем access toggle
            const shareWindowSearchToggle = document.querySelector(".shareWindowSearchToggle");
            if (shareWindowSearchToggle) {
                shareWindowSearchToggle.classList.add("shareWindowSearchToggleOn");
            }
            // Меняем title списка
            const shareWindowPeopleTitle = document.querySelector(".shareWindowPeopleTitle");
            if (shareWindowPeopleTitle) {
                shareWindowPeopleTitle.innerHTML = "Участники команды";
            }
            // action button text
            const markActionButton = document.querySelector(".shareWindowActionsItemAction");
            if (markActionButton) {
                markActionButton.innerHTML = "Пригласить";
            }
            // minor button text
            const markMinorButton = document.querySelector(".shareWindowActionsItemMinor");
            if (markMinorButton) {
                markMinorButton.innerHTML = "Закрыть";
            }
            InitAccessToggles(this.accessToggle);
        }

        // Чистим people list
        const markPeopleList = document.querySelector(".shareWindowPeopleList");
        if (markPeopleList) {
            markPeopleList.innerHTML = "";
        }

        // search remove toggle
        const shareWindowPeople = document.querySelector(".shareWindowPeople");
        if (shareWindowPeople && !shareWindowPeople.classList.contains("shareWindowPeopleSearch")) {
            shareWindowPeople.classList.add("shareWindowPeopleSearch");
        }

        this.state = "search";

        const searchInput: HTMLInputElement | null = document.querySelector(
            ".shareWindowSearchFieldInput",
        );

        const searchResult = await MakeRequest(TEAM_MEMBERS_FIND_V2, {
            token: this.data.token,
            teamId: this.additionalData ? this.additionalData.teamId : 0,
            filter: searchInput ? searchInput.value : "",
        });

        const searchCandidates: MapMembers = new Map();

        // делаем map с нужными полями
        for (let i = 0; i < searchResult.members.length; i++) {
            const member = searchResult.members[i];
            searchCandidates.set(member.accountId, {
                memberId: member.accountId,
                name: member.name,
                email: member.email,
                access: "",
            });
        }

        this.refillCurrentMembers(searchCandidates);
    }

    refillCurrentMembers(members) {
        const markMembers = document.querySelector(".shareWindowPeopleList");
        if (markMembers && this.additionalData) {
            markMembers.innerHTML = "";

            for (const memberData of members) {
                markMembers.innerHTML += applyUserTemplate(
                    memberData[1],
                    this.state,
                    this.data.mode,
                    this.additionalData,
                );
            }

            if (this.state === "search") {
                const markMembersList = document.querySelectorAll(".shareWindowPeopleListItem");

                for (let i = 0; i < markMembersList.length; i++) {
                    markMembersList[i].addEventListener("mousedown", this.searchClick, false);
                }
            }
        }
    }

    async updateMyAccess() {
        if (this.data.mode === "board") {
            const result: BoardAccessResponse = await MakeRequest(BOARD_ACCESS_GET_V2, {
                token: this.data.token,
                boardUID: this.data.boardUID,
            });
            if (this.additionalData) {
                this.additionalData.myAccess = result.access;
            }
        } else {
            const projectShare = await MakeRequest(PROJECT_ACCESS_GET_V2, {
                token: this.data.token,
                projectId: this.data.projectId,
            });
            if (this.additionalData) {
                this.additionalData.myAccess = projectShare.access;
            }
        }
    }

    /* Search --------------------------------------------------- */

    searchChange = async (e) => {
        const markSearch: HTMLInputElement | null = document.querySelector(
            ".shareWindowSearchFieldInput",
        );
        const currentValue = markSearch ? markSearch.value : "";

        const bubbles = document.querySelectorAll(".shareWindowShareAdditionalBubblesItem");

        if (currentValue === "" && bubbles.length === 0) {
            this.BuildShare();
        } else {
            await this.BuildSearch();
        }
    };

    searchInput = async (e) => {
        // Закомментировал и так не работающую фичу, она работает криво
        // if (e.key === 'Enter') {
        //     return;
        //     const markSearch: HTMLInputElement | null = document.querySelector(".shareWindowSearchFieldInput");
        //     const currentValue = markSearch !== null ? markSearch.value : "";
        //
        //     this.addBubble(currentValue, currentValue);
        // }
    };

    searchClick = (e) => {
        // Просто докидываем bubble если такого еще нет
        const item = getTargetLocal(e, "shareWindowPeopleListItem");
        if (item) {
            const memberEmail = item.getAttribute("memberEmail") || "";
            const memberName = item.getAttribute("memberName") || "";
            this.addBubble(memberName, memberEmail);
        }
    };

    addBubble(memberName: string, memberEmail: string) {
        const bubbles = document.querySelectorAll(".shareWindowShareAdditionalBubblesItem");
        for (let i = 0; i < bubbles.length; i++) {
            const candidateEmail = bubbles[i].getAttribute("candidateEmail");
            if (memberEmail === candidateEmail) {
                return;
            }
        }

        const shareWindowShareAdditionalBubbles = document.querySelector(
            ".shareWindowShareAdditionalBubbles",
        );
        if (shareWindowShareAdditionalBubbles) {
            const recentBubble = document.createElement("div");
            recentBubble.classList.add("shareWindowWrapper");
            shareWindowShareAdditionalBubbles.append(recentBubble);
            recentBubble.outerHTML = applySearchBubbleTemplate(memberName, memberEmail);

            for (let i = 0; i < bubbles.length; i++) {
                const candidateEmail = bubbles[i].getAttribute("candidateEmail");
                if (memberEmail === candidateEmail) {
                    bubbles[i].addEventListener("mousedown", this.closeBubble, false);
                }
            }
        }
    }

    closeBubble = (e) => {
        const bubble = getTargetLocal(e, "shareWindowShareAdditionalBubblesItem");
        if (bubble) {
            bubble.outerHTML = "";
        }
        const bubbles = document.querySelectorAll(".shareWindowShareAdditionalBubblesItem");
        const searchInput: HTMLInputElement | null = document.querySelector(
            ".shareWindowSearchFieldInput",
        );
        const inputValue = searchInput ? searchInput.value : "";
        if (bubbles.length === 0 && inputValue === "") {
            this.BuildShare();
        }
    };

    /* Proccessors ------------------------------------------------------------- */
    accessToggle = (e) => {
        const toggle = getTargetLocal(e, "unionAccessToggle");
        if (toggle) {
            const reason = toggle.getAttribute("reason");
            if (reason && Object.prototype.hasOwnProperty.call(ReasonAccess, reason)) {
                const accessMenuData = {
                    toggle: toggle,
                    proccessor: this.accessToggleResult,
                    reason: reason,
                    items: getAccessMenuData(reason, this.data.onlyRead),
                };

                this.data.accessSelector(accessMenuData);
            }
        }
    };

    accessToggleResult = async (requestData, resultItem) => {
        if (requestData.reason === "teamAccess") {
            let right: AccessByLinkOrTeam = "denied";
            if (resultItem.code === "edit") {
                right = "edit";
            } else if (resultItem.code === "view") {
                right = "view";
            } else if (resultItem.code === "view_copy") {
                right = "view_copy";
            }

            if (this.additionalData && "teamName" in this.additionalData) {
                this.additionalData.accessByTeam = right;
            }

            // await this.data["HTTPRequest"] чтобы поменять доступ для команды на right
            await MakeRequest(
                BOARD_SHARE_TEAM_ACCESS_SET,
                {
                    token: this.data.token,
                    boardUID: this.data.boardUID,
                    access: right,
                },
                true,
            );

            await this.updateMyAccess();

            if (
                this.additionalData &&
                this.additionalData.myAccess !== "owner" &&
                this.additionalData.myAccess !== "edit"
            ) {
                await this.data.stateChange();
                this.close();
            }
        } else if (requestData.reason === "publicAccess") {
            let right: AccessByLinkOrTeam = "denied";
            if (resultItem["code"] === "edit") {
                right = "edit";
            } else if (resultItem["code"] === "view") {
                right = "view";
            } else if (resultItem["code"] === "view_copy") {
                right = "view_copy";
            }
            if (this.additionalData && "teamName" in this.additionalData) {
                this.additionalData.accessByLink = right;
            }
            // await this.data["HTTPRequest"] чтобы поменять доступ по ссылке на right
            await MakeRequest(
                BOARD_SHARE_LINK_ACCESS_SET_V2,
                {
                    token: this.data.token,
                    boardUID: this.data.boardUID,
                    access: right,
                },
                true,
            );

            await this.updateMyAccess();

            this.reloadPasswordSpace();

            if (
                this.additionalData &&
                this.additionalData.myAccess !== "owner" &&
                this.additionalData.myAccess !== "edit"
            ) {
                await this.data.stateChange();
                this.close();
            }
        } else if (requestData.reason === "memberAccess") {
            const memberItem = requestData.toggle.closest(".shareWindowPeopleListItem");
            const memberId = Number.parseInt(memberItem.getAttribute("memberId"));

            if (resultItem.code === "delete") {
                let handler = "";
                let payload = {};
                if (this.data["mode"] === "board") {
                    handler = BOARD_MEMBERS_DELETE_V2;
                    payload = {
                        token: this.data.token,
                        boardUID: this.data.boardUID,
                        memberId: memberId,
                    };
                } else {
                    handler = PROJECT_MEMBERS_DELETE_V2;
                    payload = {
                        token: this.data.token,
                        projectId: this.data.projectId,
                        memberId: memberId,
                    };
                }

                await MakeRequest(handler, payload, true);

                this.members.delete(memberId);
                this.BuildShare();
            } else {
                let right: MemberMap["access"] = "";
                if (resultItem.code === "edit") {
                    right = "edit";
                } else if (resultItem.code === "view") {
                    right = "view";
                } else if (resultItem.code === "view_copy") {
                    right = "view_copy";
                } else if (resultItem.code === "owner") {
                    right = "owner";
                }

                const memberData = this.members.get(memberId);

                if (memberData) {
                    memberData.access = right;
                }

                let handler = "";
                let payload = {};
                if (this.data.mode === "board") {
                    handler = BOARD_MEMBERS_ACCESS_SET_V2;
                    payload = {
                        token: this.data.token,
                        boardUID: this.data.boardUID,
                        memberId: memberId,
                        access: right,
                    };
                } else {
                    handler = PROJECT_MEMBERS_ACCESS_SET_V2;
                    payload = {
                        token: this.data.token,
                        projectId: this.data.projectId,
                        memberId: memberId,
                        access: right,
                    };
                }

                await MakeRequest(handler, payload, true);
            }

            await this.updateMyAccess();

            if (this.data.mode === "board") {
                if (
                    this.additionalData &&
                    this.additionalData.myAccess !== "owner" &&
                    this.additionalData.myAccess !== "edit"
                ) {
                    await this.data.stateChange();
                    this.close();
                }
            } else {
                if (
                    this.additionalData &&
                    "myAccountId" in this.additionalData &&
                    this.additionalData.myAccountId === memberId
                ) {
                    await this.data.stateChange();
                    this.close();
                }
            }
        } else if (requestData.reason === "searchAccess") {
            let right: AccessByLinkOrTeam;
            if (resultItem.code === "edit") {
                right = "edit";
            } else if (resultItem.code === "view_copy") {
                right = "view_copy";
            } else {
                right = "view";
            }
            if (this.additionalData) {
                this.additionalData.searchAccess = right;
            }
        }
    };

    wrapperClick = (e) => {
        const tryInner = getTargetLocal(e, "shareWindow");
        if (tryInner === null) {
            this.close();
        }
    };

    /* Actions ------------------------------------------------------------- */

    actionClick = async (e) => {
        if (this.state === "search") {
            const candidates = getCandidatesList();
            if (candidates.length === 0) return;
            if (this.data.mode === "board") {
                // this.data["HTTPRequest"] чтобы пригласить candidates в команду и на доску
                await MakeRequest(
                    BOARD_MEMBERS_ADD_V2,
                    {
                        token: this.data.token,
                        boardUID: this.data.boardUID,
                        members: candidates,
                        access: this.additionalData?.searchAccess,
                    },
                    true,
                );
            } else {
                // this.data["HTTPRequest"] чтобы пригласить candidates в проект
                await MakeRequest(
                    PROJECT_MEMBERS_ADD_V2,
                    {
                        token: this.data.token,
                        projectId: this.data.projectId,
                        members: candidates,
                        access: this.additionalData?.searchAccess,
                    },
                    true,
                );
            }
            notify("Новые пользователи добавлены", this.data.notificator);

            this.close();
        } else {
            if (this.data.mode === "board") {
                if (
                    navigator.permissions !== undefined &&
                    navigator.permissions.query !== undefined
                ) {
                    navigator.permissions
                        .query({ name: "clipboard-write" as PermissionName })
                        .then((result) => {
                            if (result.state === "granted" || result.state === "prompt") {
                            }
                        });
                }

                navigator.clipboard.writeText(`${this.data.host}/board/${this.data.boardUID}`).then(
                    () => {},
                    () => {},
                );
                notify("Скопировано", this.data.notificator);
            } else {
                if (
                    (this.additionalData && this.additionalData.myAccess === "owner") ||
                    (this.additionalData && this.additionalData.myAccess === "edit")
                ) {
                    const markProjectName: HTMLInputElement | null = document.querySelector(
                        ".shareWindowTopProjectNameFieldInput",
                    );
                    await MakeRequest(
                        PROJECT_NAME_SET_V2,
                        {
                            projectId: this.data.projectId,
                            name: markProjectName ? markProjectName.value : "",
                            token: this.data.token,
                        },
                        true,
                    );
                    await this.data.stateChange();
                    notify("Сохранено", this.data.notificator);
                }
                this.close();
            }
        }
    };

    minorClick = async (e) => {
        if (this.data.mode === "board") {
            this.close();
        } else {
            if (this.state === "search") {
                this.close();
            } else {
                if (this.additionalData && this.additionalData.myAccess === "owner") {
                    await MakeRequest(PROJECT_REMOVE_V2, {
                        token: this.data.token,
                        projectId: this.data.projectId,
                    });

                    notify("Проект удален", this.data.notificator);
                    await this.data.stateChange();
                    this.close();
                } else {
                    if (this.additionalData && "myAccountId" in this.additionalData) {
                        MakeRequest(PROJECT_MEMBERS_DELETE_V2, {
                            token: this.data.token,
                            memberId: this.additionalData.myAccountId,
                            projectId: this.data.projectId,
                        });

                        notify("Вы покинули проект", this.data.notificator);
                        await this.data.stateChange();
                    }

                    this.close();
                }
            }
        }
    };

    /* Password ----------------------------------------------------- */

    applyPasswordAdd() {
        const passSpace = document.querySelector(".shareWindowShareAdditionalMoreOptionPass");
        if (passSpace) {
            passSpace.innerHTML = getPasswordAddTemplate();
        }
        const addButton = document.querySelector(".shareWindowShareAdditionalMoreOptionPassAdd");
        if (addButton) {
            addButton.addEventListener("mousedown", this.applyPasswordEditor, false);
        }
    }

    applyPasswordEditor = () => {
        const passSpace = document.querySelector(".shareWindowShareAdditionalMoreOptionPass");
        if (passSpace) {
            passSpace.innerHTML = getPasswordEditorTemplate();
        }
        const saveButton = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassEditItemApply",
        );
        if (saveButton) {
            saveButton.addEventListener("mousedown", this.savePassword, false);
        }
        const closeButton = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassEditItemClose",
        );
        if (closeButton) {
            closeButton.addEventListener("mousedown", this.reloadPasswordSpace, false);
        }
        const passInput = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassEditInput",
        );
        if (passInput) {
            passInput.addEventListener("focus", () => this.inputState(1), false);
            passInput.addEventListener("blur", () => this.inputState(0), false);
        }
    };

    savePassword = async () => {
        const passInput: HTMLInputElement | null = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassEditInput",
        );
        const errorDiv = document.querySelector(".shareWindowShareAdditionalMoreOptionPassError");

        if (passInput) {
            const passwordValue = passInput ? passInput.value : "";
            const { hasError, errorText } = validatePassword(passwordValue);

            if (hasError) {
                if (errorDiv) {
                    errorDiv.textContent = errorText;
                } else {
                    const newErrorDiv = document.createElement("div");
                    newErrorDiv.className = "shareWindowShareAdditionalMoreOptionPassError";
                    newErrorDiv.textContent = errorText;
                    if (passInput.parentNode) {
                        passInput.parentNode.appendChild(newErrorDiv);
                    }
                }
                return;
            }

            if (errorDiv) {
                errorDiv.remove();
            }
            await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2, {
                token: this.data.token,
                boardUID: this.data.boardUID,
                password: passwordValue,
            });

            this.reloadPasswordSpace();
        }
    };

    applyPasswordChange() {
        const passSpace = document.querySelector(".shareWindowShareAdditionalMoreOptionPass");
        if (passSpace) {
            passSpace.innerHTML = getPasswordChangeTemplate();
        }
        const changePassword = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassChangeAction",
        );
        if (changePassword) {
            changePassword.addEventListener("mousedown", this.applyPasswordEditor, false);
        }
        const deleteButton = document.querySelector(
            ".shareWindowShareAdditionalMoreOptionPassChangeDelete",
        );
        if (deleteButton) {
            deleteButton.addEventListener("mousedown", this.deletePassword, false);
        }
    }

    deletePassword = async () => {
        await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2, {
            token: this.data.token,
            boardUID: this.data.boardUID,
            password: "",
        });
        this.reloadPasswordSpace();
    };

    reloadPasswordSpace = async () => {
        const additionalData = this.additionalData;
        if (
            additionalData &&
            "accessByLink" in additionalData &&
            additionalData.accessByLink !== "edit" &&
            additionalData.accessByLink !== "view" &&
            additionalData.accessByLink !== "view_copy"
        ) {
            const passSpace = document.querySelector(".shareWindowShareAdditionalMoreOptionPass");
            if (passSpace) {
                passSpace.innerHTML = "";
            }
            return;
        }

        const passInfo: PasswordGetResponse = await MakeRequest(
            BOARD_SHARE_LINK_ACCESS_PASSWORD_GET_V2,
            {
                token: this.data.token,
                boardUID: this.data.boardUID,
            },
        );

        if (passInfo.password === "") {
            this.applyPasswordAdd();
        } else {
            this.applyPasswordChange();
        }
    };

    /*  ------------------------------------------------------------- */

    private state: State = "start";
    private data: BoardModeData;
    private members: MapMembers = new Map();
    additionalData: AdditionalData | null = null;
}
